import { IdentityUserAri } from '@atlassian/ari/identity/user';
// FIXME circular dependency
// eslint-disable-next-line workspaces/require-dependency
import { presenceState } from '@databases/presence';
import { captureException } from '@sentry/core';
import DataLoader from 'dataloader';
import { proxy } from 'valtio';
import { type Awareness } from 'y-protocols/awareness';

import type { UserProfileIdentity } from '@canvas/atlassian-client/identity/types';
import { type CanvasIdentity, type Identity, IdentityKind } from '@canvas/model/identity';
import { type ClientId } from '@canvas/model/telepointers';

import { createParticipantFromIdentity } from '../../presence/types';

export const awarenessFields = {
  telepointers: 't2',
  identity: 'i',
} as const;

interface AwarenessState {
  t2: [string, string];
  i: string;
}

export type TelepointerState = {
  identity: Identity;
  entryId: string;
  fieldId: string;
};

export class YDatabaseTelepointers {
  telepointers: Record<number, TelepointerState> = proxy({});
  participants: Record<number, Identity> = proxy({});
  fullIdentityProfiles: DataLoader<string, UserProfileIdentity | undefined> | null;
  #localIdentity: Identity | null = null;

  /**
   * @param awareness The awareness object to sync telepointers with.
   * @param identitySupplier Provides the account ID of the current user.
   * @param userSupplier Provides full account details for a given ste of account IDs.
   */
  constructor(
    public awareness: Awareness,
    identitySupplier: (() => Promise<Identity>) | null,
    userSupplier:
      | ((accountIds: readonly string[]) => Promise<(UserProfileIdentity | undefined)[]>)
      | null
  ) {
    this.fullIdentityProfiles = userSupplier
      ? new DataLoader(userSupplier, { maxBatchSize: 90 })
      : null;
    awareness.on('change', this.onAwarenessUpdate);

    identitySupplier?.().then((identity) => {
      setAccountId(identity);
      this.#localIdentity = identity;
      this.awareness.setLocalStateField(awarenessFields.identity, identity.accountId);
    });

    this.loadExistingAwarenessStates();
  }

  setCursorPosition(entryId?: string, fieldId?: string) {
    this.awareness.setLocalStateField(awarenessFields.telepointers, [entryId, fieldId]);
  }

  dispose() {
    // Clear DataLoader cache
    this.fullIdentityProfiles?.clearAll();
    this.fullIdentityProfiles = null;

    // Clear proxy objects
    for (const key in this.telepointers) {
      delete this.telepointers[Number(key)];
    }
    for (const key in this.participants) {
      delete this.participants[Number(key)];
    }

    this.awareness.off('change', this.onAwarenessUpdate);
    this.awareness.destroy();
  }

  /**
   * Both field and entry id are optional. If one is provided and the other omitted, it will be assumed the whole field/entry is selected.
   * @param clientId
   * @param identity
   * @param entryId
   * @param fieldId
   */
  public updatePresenceState(
    clientId: number,
    identity: Identity,
    fieldId: string,
    entryId: string
  ) {
    const isLocal = process.env.NODE_ENV === 'development';
    if (
      !isLocal &&
      this.#localIdentity &&
      this.isSameAccount(identity, this.#localIdentity)
    ) {
      return;
    }

    presenceState.addParticipant(
      { fieldId, entryId },
      createParticipantFromIdentity(identity, clientId)
    );
  }

  private extractAccountIdFromAriFormat(ariOrAccountId: string): string {
    if (IdentityUserAri.check(ariOrAccountId)) {
      return IdentityUserAri.parse(ariOrAccountId).resourceId;
    }
    return ariOrAccountId;
  }

  private isSameAccount(identity1: Identity, identity2: Identity): boolean {
    return (
      this.extractAccountIdFromAriFormat(identity1.accountId) ===
      this.extractAccountIdFromAriFormat(identity2.accountId)
    );
  }

  private loadExistingAwarenessStates() {
    const states = this.awareness.getStates();
    const existingClientIds = Array.from(states.keys()).filter(
      (clientId) => clientId !== this.awareness.clientID
    );

    if (existingClientIds.length > 0) {
      this.onAwarenessUpdate(
        { added: existingClientIds, updated: [], removed: [] },
        'initialization'
      );
    }
  }

  private onAwarenessUpdate = (
    { added: addedClientIds, updated: updatedClientIds, removed: deletedClientIds }: any,
    origin: string
  ) => {
    if (origin !== 'local') {
      const states = this.awareness.getStates();

      for (const clientId of [...addedClientIds, ...updatedClientIds]) {
        if (clientId === this.awareness.clientID) {
          continue;
        }

        const accountId =
          typeof states.get(clientId)?.[awarenessFields.identity] === 'string'
            ? states.get(clientId)?.[awarenessFields.identity]
            : // For backwards compatibility with older clients
              states.get(clientId)?.[awarenessFields.identity]?.accountId;

        if (!accountId) {
          continue;
        }

        // @TODO: Make telepointers support anonymous users by not attempting to fetch or construct
        // the partial identity for anonymous users, but instead just set the participant and telepointer
        // with the Anonymous Identity type
        this.fullIdentityProfiles
          ?.load(accountId)
          .then((identityProfile) => {
            // We need to check state again, in case it has since been deleted
            if (identityProfile) {
              // If we were able to load the full profile, we update the participant and
              // telepointer with their full identity
              const identity = this.getIdentityFromProfile(identityProfile);
              this.setParticipantsAndTelepointers(clientId, identity);
            }
          })
          .catch((e) => captureException(e));
      }

      const participantsToRemove = [];
      for (const clientId of deletedClientIds) {
        const identity = this.participants[clientId];
        if (identity) {
          const participant = createParticipantFromIdentity(identity, clientId);
          participantsToRemove.push(participant);
        }
        delete this.telepointers[clientId];
        delete this.participants[clientId];
      }

      // Do a single batch update after the loop
      if (participantsToRemove.length > 0) {
        participantsToRemove.forEach((participant) => {
          presenceState.removeParticipant(participant);
        });
      }
    }
  };

  private getIdentityFromProfile(identityProfile: UserProfileIdentity): CanvasIdentity {
    return {
      accountId: identityProfile.id,
      displayName: identityProfile.name,
      nickname: identityProfile.nickname ?? identityProfile.name,
      picture: identityProfile.picture,
      email: identityProfile.email,
      locale: identityProfile.locale,
      type: IdentityKind.CanvasId,
    } satisfies CanvasIdentity;
  }

  /**
   * Given a clientId and an identity, sets the participant and telepointer state
   * @param clientId The clientId of the participant
   * @param identity The identity of the participant
   */
  private setParticipantsAndTelepointers(clientId: ClientId, identity: Identity) {
    const state = this.awareness.getStates().get(clientId) as AwarenessState;

    const identityState = state?.[awarenessFields.identity];
    if (identityState) {
      this.participants[clientId] = identity;
    }

    const telepointers = state?.[awarenessFields.telepointers];
    if (Array.isArray(telepointers)) {
      const [entryId, fieldId] = telepointers;
      this.updatePresenceState(clientId, identity, fieldId, entryId);
      this.telepointers[clientId] = {
        entryId,
        fieldId,
        identity,
      };
    }
  }
}

// For backwards compat with old clients
function setAccountId(identity: Identity | undefined) {
  if (identity && 'account_id' in identity) {
    identity.accountId = identity.account_id as string;
  }
}
